import { EditOutlined, ShoppingCartOutlined, BarChartOutlined } from "@ant-design/icons";
import { Alert, Button, Input, Skeleton, Switch, Typography } from "antd";
import CustomCard from "components/CustomCard";
import CustomModal from "components/CustomModal";
import { useProductSoldDisplay } from "hooks/product/productSoldDisplay";
import { useEffect, useRef, useState } from "react";
import { notify } from "utils/helperFuncs";
import {
  formatSoldCount,
  formatSoldCountPreview,
  getSoldCountPreview,
  isValidExtraSoldCount,
  SOLD_DISPLAY_MESSAGES as M,
} from "./utils";
import "./index.scss";

const ProductSoldDisplay = ({ product, active, canUpdate, onSaved }) => {
  const { data, loading, error, updateDisplayMode, updateExtraSoldCount } = useProductSoldDisplay({
    productId: product?.id,
    productCode: product?.code,
    enabled: !!(active && canUpdate),
  });
  const [popup, setPopup] = useState(false);
  const [confirmHide, setConfirmHide] = useState(false);
  const [input, setInput] = useState("");
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(null);
  const busy = useRef(false);
  useEffect(() => {
    setPopup(false);
    setConfirmHide(false);
    setInput("");
    setTouched(false);
  }, [active, product?.id]);

  const refreshList = () => {
    Promise.resolve(onSaved?.()).catch(() => undefined);
  };
  const saveMode = async (mode) => {
    if (!canUpdate || busy.current) return;
    busy.current = true;
    setSaving("mode");
    try {
      await updateDisplayMode(mode);
      notify.success({ message: M.displayUpdated });
      refreshList();
    } catch (err) {
      notify.error({ message: M.updateFailed });
    } finally {
      setConfirmHide(false);
      busy.current = false;
      setSaving(null);
    }
  };
  const saveExtra = async () => {
    if (!canUpdate || busy.current || !isValidExtraSoldCount(input)) return;
    busy.current = true;
    setSaving("extra");
    try {
      await updateExtraSoldCount(input);
      setPopup(false);
      notify.success({ message: M.extraUpdated });
      refreshList();
    } catch (err) {
      notify.error({ message: err.message });
    } finally {
      busy.current = false;
      setSaving(null);
    }
  };
  const closePopup = () => {
    if (!busy.current) setPopup(false);
  };
  if (!canUpdate || !active) return null;
  const valid = isValidExtraSoldCount(input);
  const inputError = input ? !valid && M.extraInvalid : touched && M.extraRequired;
  const total = data ? data.actual_sold_count + data.extra_sold_count : 0;
  const number = (count) => formatSoldCount(count).replace(/ sp$/, "");
  const preview =
    data && formatSoldCountPreview(getSoldCountPreview(data.actual_sold_count, data.extra_sold_count, input));

  return (
    <div className="product-sold-display">
      <CustomCard
        title={
          <Typography.Text strong style={{ color: "#052987" }}>
            Thông tin hiển thị trên App
          </Typography.Text>
        }
        content={
          loading ? (
            <div aria-label="Đang tải lượt bán">
              <Skeleton active paragraph={{ rows: 3 }} />
            </div>
          ) : error ? (
            <Alert type="error" message={error.message} />
          ) : (
            data && (
              <div className="sold-display-content">
                <div className="sold-display-card">
                  <div className="sold-display-summary">
                    <span className="sold-display-dot" />
                    <div>
                      <div>
                        Hiển thị lượt bán trên App: <strong>{formatSoldCount(total)}</strong>
                      </div>
                      <div className="sold-display-formula">
                        Lượt bán ghi nhận ({number(data.actual_sold_count)}) + Lượt bán bổ sung (
                        {number(data.extra_sold_count)})
                      </div>
                      {data.display_mode === "show" && total === 0 && (
                        <div className="sold-display-formula">{M.zeroDisplay}</div>
                      )}
                    </div>
                  </div>
                  <Switch
                    size="small"
                    aria-label="Hiển thị lượt bán trên App"
                    checked={data.display_mode === "show"}
                    disabled={!!saving}
                    loading={saving === "mode"}
                    onChange={(checked) => {
                      if (busy.current) return;
                      if (checked) saveMode("show");
                      else setConfirmHide(true);
                    }}
                  />
                </div>
                <div className="sold-display-row">
                  <span>Lượt bán ghi nhận:</span>
                  <strong>{formatSoldCount(data.actual_sold_count)}</strong>
                </div>
                <div className="sold-display-row">
                  <span>
                    Lượt bán bổ sung{" "}
                    <Button
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      aria-label="Cập nhật lượt bán bổ sung"
                      disabled={!!saving}
                      onClick={() => {
                        setInput("");
                        setTouched(false);
                        setPopup(true);
                      }}
                    />
                  </span>
                  <strong>{formatSoldCount(data.extra_sold_count)}</strong>
                </div>
              </div>
            )
          )
        }
      />
      {confirmHide && (
        <CustomModal
          selfVisibleControlled={false}
          visible
          footer={null}
          title={M.hideTitle}
          message={M.hideDescription}
          isBlockCloseOnOke
          cancelButtonLabel={M.cancel}
          okeButtonLabel={M.confirm}
          buttonLoading={saving === "mode"}
          loadingBtnCancel={!!saving}
          closable={!saving}
          maskClosable={!saving}
          keyboard={!saving}
          onOke={() => saveMode("hidden")}
          onCancel={() => {
            if (!busy.current) setConfirmHide(false);
          }}
        />
      )}
      {popup && data && (
        <CustomModal
          className="product-sold-display-modal"
          selfVisibleControlled={false}
          visible
          width={564}
          centered
          footer={null}
          title={
            <div>
              <div>Cập nhật số lượng hiển thị</div>
              <div className="sold-display-subtitle">Sản phẩm {product?.name}</div>
            </div>
          }
          hideCancelButton
          hideConfirmButton
          addMoreButton={
            <>
              <Button disabled={!!saving} onClick={closePopup}>
                Hủy bỏ
              </Button>
              <Button
                type="primary"
                htmlType="button"
                disabled={!valid || !!saving}
                loading={saving === "extra"}
                onClick={saveExtra}
              >
                Lưu lại
              </Button>
            </>
          }
          isBlockCloseOnOke
          buttonLoading={saving === "extra"}
          loadingBtnCancel={!!saving}
          closable={!saving}
          maskClosable={!saving}
          keyboard={!saving}
          onCancel={closePopup}
          onOke={saveExtra}
        >
          <div className="sold-display-popup-content">
            <div>
              <strong>Xem trước hiển thị lượt bán trên app</strong>
              <div className="sold-display-preview">
                <strong>Hiển thị trên app đại lý</strong>
                {preview === M.zeroPreview ? (
                  <span className="sold-display-subtitle">{preview}</span>
                ) : (
                  <span>
                    <ShoppingCartOutlined style={{ fontSize: 14 }} /> Đã bán: <strong>{preview} sản phẩm</strong>
                  </span>
                )}
              </div>
              <div className="sold-display-formula">
                Số lượt Hiển thị trên App đại lý = Số lượt bán ghi nhận + Số lượt bán bổ sung
              </div>
            </div>
            <div className="sold-display-recorded">
              <BarChartOutlined />
              <div>
                <strong>Lượt bán ghi nhận</strong>
                <div>{number(data.actual_sold_count)} sản phẩm</div>
              </div>
            </div>
            <div>
              <label htmlFor={`sold-extra-${product.id}`}>
                <strong>Lượt bán bổ sung mới</strong>
              </label>
              <Input
                id={`sold-extra-${product.id}`}
                type="text"
                inputMode="numeric"
                value={input}
                placeholder={M.placeholder}
                disabled={!!saving}
                aria-invalid={!!inputError}
                aria-describedby={inputError ? `sold-extra-error-${product.id}` : undefined}
                className={inputError ? "sold-display-invalid" : ""}
                onChange={(event) => {
                  setInput(event.target.value);
                  setTouched(true);
                }}
                onBlur={() => setTouched(true)}
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter" ||
                    (!event.ctrlKey &&
                      !event.metaKey &&
                      !event.altKey &&
                      event.key.length === 1 &&
                      !/^[0-9]$/.test(event.key))
                  )
                    event.preventDefault();
                }}
              />
              {inputError && (
                <div className="sold-display-error" id={`sold-extra-error-${product.id}`} role="alert">
                  {inputError}
                </div>
              )}
              <div className="sold-display-subtitle">
                Lượt bán bổ sung hiện tại: {formatSoldCount(data.extra_sold_count)}
              </div>
            </div>
          </div>
        </CustomModal>
      )}
    </div>
  );
};
export default ProductSoldDisplay;
